/**
 * Component: App
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R01, FRONTEND-WEB-V1-R06, FRONTEND-WEB-V1-R10,
 *               FRONTEND-WEB-V1-R11
 *
 * Root application component with provider setup.
 * FRONTEND-WEB-V1-R06: Graceful error handling with error boundaries.
 * FRONTEND-WEB-V1-R10: Theme provider for light/dark mode toggle.
 * FRONTEND-WEB-V1-R11: Runtime toggling of UI modules via configuration feature flags
 * and internationalization support.
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { Suspense, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { AppRoutes } from './routes';
import { BrandingProvider, FeatureFlagProvider, ThemeProvider } from './contexts';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuthStore } from './stores/auth-store';

// Initialize i18n (FRONTEND-WEB-V1-R11)
import './i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * Loading fallback for i18n Suspense.
 * FRONTEND-WEB-V1-R11: Show loading state while translations load.
 */
function I18nLoadingFallback(): React.ReactElement {
  return (
    <div
      className="flex h-screen w-screen items-center justify-center"
      role="status"
      aria-label="Loading translations"
    >
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

/**
 * Auth initializer component
 * AUTH-V1-R06: Initialize auth store on app startup to restore persisted session state.
 */
function AppInitializer({ children }: { children: React.ReactNode }): React.ReactElement {
  const loadStoredAuth = useAuthStore((state) => state.loadStoredAuth);

  useEffect(() => {
    loadStoredAuth();
  }, [loadStoredAuth]);

  return <>{children}</>;
}

/**
 * App component
 *
 * FRONTEND-WEB-V1-R06: ErrorBoundary wraps the entire application to catch
 * and gracefully handle JavaScript errors.
 * FRONTEND-WEB-V1-R10: ThemeProvider enables light/dark mode toggle.
 * FRONTEND-WEB-V1-R11: FeatureFlagProvider wraps the entire application
 * to provide runtime feature flag state for UI module toggling.
 * Suspense boundary handles i18n async loading.
 */
function App(): React.ReactElement {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Suspense fallback={<I18nLoadingFallback />}>
          <QueryClientProvider client={queryClient}>
            <FeatureFlagProvider>
              <BrandingProvider>
                <BrowserRouter>
                  <AppInitializer>
                    <AppRoutes />
                  </AppInitializer>
                </BrowserRouter>
              </BrandingProvider>
            </FeatureFlagProvider>
          </QueryClientProvider>
        </Suspense>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
